import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MessageCircle, Check, X, ExternalLink, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";

export default function WhatsAppNotificationsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/whatsapp", { params: { status: "pending" } });
      setItems(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const send = async (it) => {
    // Open WhatsApp link in new tab
    window.open(it.wa_url, "_blank", "noopener,noreferrer");
    // Optimistically mark as sent
    try {
      await api.post(`/notifications/whatsapp/${it.id}/mark-sent`);
      toast.success("Lajmërimi u dërgua");
      load();
    } catch {
      toast.error("Gabim gjatë shënimit");
    }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/notifications/whatsapp/${id}/dismiss`);
      load();
    } catch {
      toast.error("Gabim");
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="surface-card p-5" data-testid="wa-notifications-widget">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Bell size={16} className="text-[#25D366]" />
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute h-full w-full rounded-full bg-[#25D366] opacity-75"></span>
            <span className="relative h-3 w-3 rounded-full bg-[#25D366]"></span>
          </span>
        </div>
        <h3 className="font-semibold">Lajmërime WhatsApp në Pritje</h3>
        <span className="ml-auto rounded-full bg-[#25D366]/15 text-[#1d8d4d] text-[10px] font-bold px-2 py-0.5">
          {items.length}
        </span>
      </div>
      <p className="text-xs text-[#5C5C5C] mb-3">
        Klikoni <b>Dërgo</b> për të hapur WhatsApp me mesazhin gati. Pas dërgimit, lajmërimi shënohet automatikisht.
      </p>
      <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {items.map((it) => (
          <li key={it.id} className="rounded-lg border border-[#E2DFD8] bg-white p-3" data-testid={`wa-notif-${it.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">
                  <MessageCircle size={12} className="text-[#25D366] shrink-0" />
                  Te: <span className="font-mono text-xs">{it.doctor_name}</span>
                  <span className="text-xs text-[#5C5C5C]">({it.to_phone})</span>
                </div>
                <pre className="text-xs whitespace-pre-wrap text-[#1A1A1A] bg-[#FDFBF7] mt-1.5 p-2 rounded border border-[#E2DFD8] max-h-32 overflow-y-auto">
                  {it.text}
                </pre>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => send(it)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] text-white px-3 py-1.5 text-xs font-medium hover:bg-[#1ebe5b]"
                data-testid={`wa-send-${it.id}`}
              >
                <ExternalLink size={12} />
                Dërgo në WhatsApp
              </button>
              <button
                onClick={() => dismiss(it.id)}
                className="rounded-md border border-[#E2DFD8] text-[#5C5C5C] px-3 py-1.5 text-xs hover:text-[#9E3C3C] hover:border-[#9E3C3C]"
                data-testid={`wa-dismiss-${it.id}`}
                title="Anulo"
              >
                <X size={12} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
